import React from 'react'
import { pathOr, compose } from 'ramda'
import { Col, Spin, message, Form, Modal } from 'antd'
import { setToStorage } from 'utils/localStorage'
import {
  getFromSessionStorage,
  removeFromSessionStorage
} from 'utils/sessionStorage'
import { CLOSE_INACTIVITY_MODALS_AFTER_LOGOUT } from 'components/InactivityModal/constants'
import useSignIn from 'apollo/mutations/SIGN_IN'
import logo from 'assets/logo.svg'
import { Input, Password } from 'ui/styled'
import logger from 'utils/logger'

import {
  Root,
  LoginLogo,
  LogoImage,
  LoginTitle,
  SignInContainer,
  SignInRow,
  PrimaryButton
} from './styled'

const FormItem = Form.Item

const setTokenFromData = compose(
  setToStorage('token'),
  pathOr('', ['advocateLoginWithEmail', 'token'])
)

const setRefreshTokenFromData = compose(
  setToStorage('refresh'),
  pathOr('', ['advocateLoginWithEmail', 'refresh_token'])
)

const setTokenExpiresAtIsoFromData = compose(
  setToStorage('tokenExpiresAtIso'),
  pathOr('', ['advocateLoginWithEmail', 'token_expires_at_iso'])
)

const SignIn = () => {
  const [form] = Form.useForm()
  const { onSignIn, loading } = useSignIn()

  const onFinish = async variables => {
    try {
      const {
        r: { data }
      } = await onSignIn(variables)

      const {
        message: errorMessage,
        locked,
        remained_attempts,
        authenticated,
        user
      } = pathOr({}, ['advocateLoginWithEmail'], data)

      if (remained_attempts === 2 || remained_attempts === 1) {
        logger.logInfo(
          `Failed authentication attempt, ${remained_attempts} tries remain`,
          { email: variables.email }
        )
        Modal.error({
          content: errorMessage
        })
        return
      }

      if (locked) {
        logger.logWarning(`Failed authentication attempt, account is locked`, {
          email: variables.email,
          message: errorMessage
        })
        Modal.error({
          title: 'Your account is locked',
          content: errorMessage
        })
        return
      }

      if (!authenticated) {
        logger.logInfo(`Failed authentication attempt`, {
          email: variables.email
        })
        Modal.error({
          title: 'You have problems with authentication',
          content: errorMessage,
          wrapProps: { 'data-test': 'signInAuthErrorModal' },
          okButtonProps: { 'data-test': 'signInModalOkBtn' }
        })
        return
      }

      const userId = pathOr(null, ['id'], user)
      if (userId) {
        logger.logInfo(`Successful authentication for ${user?.full_name}`, {
          id: userId,
          name: user?.full_name
        })
      }

      setTokenFromData(data)
      setRefreshTokenFromData(data)
      setTokenExpiresAtIsoFromData(data)
      // rebuild client with existing user token
      const lastVisitedPage = getFromSessionStorage('lastVisitedPage')()
      if (lastVisitedPage) {
        logger.logInfo(`Forwarded to last visited page`, {
          url: lastVisitedPage
        })
        window.location.replace(lastVisitedPage)
        const broadcastChannel = new BroadcastChannel('inactivity-broadcast')
        broadcastChannel.postMessage({
          type: CLOSE_INACTIVITY_MODALS_AFTER_LOGOUT
        })
        broadcastChannel.close()
        removeFromSessionStorage('lastVisitedPage')
      } else {
        window.location.replace('/')
      }
    } catch (error) {
      message.error(error.message)
      logger.logError(error.message, {}, error)
    }
  }

  return (
    <Root>
      <SignInContainer>
        <LoginLogo>
          <LogoImage src={logo} alt="Rightway Healthcare" />
        </LoginLogo>
        <LoginTitle>Welcome back</LoginTitle>
        <SignInRow type="flex" align="middle" justify="center">
          <Col>
            <Spin spinning={loading} size="large">
              <Form form={form} onFinish={onFinish}>
                <FormItem
                  name="email"
                  rules={[
                    {
                      type: 'email',
                      message: 'The input is not valid E-mail!'
                    },
                    {
                      required: true,
                      message: 'Please input your E-mail!'
                    }
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="E-mail"
                    data-test="signInEmailString"
                    width="330px"
                  />
                </FormItem>
                <FormItem
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your Password!'
                    }
                  ]}
                >
                  <Password
                    size="large"
                    placeholder="Password"
                    data-test="signInPasswordString"
                  />
                </FormItem>
                <FormItem>
                  <PrimaryButton
                    size="large"
                    type="primary"
                    htmlType="submit"
                    block
                    data-test="signInLogInBtn"
                  >
                    Log In
                  </PrimaryButton>
                </FormItem>
              </Form>
            </Spin>
          </Col>
        </SignInRow>
      </SignInContainer>
    </Root>
  )
}

export default SignIn
